import { useState, useCallback } from 'react';
import {
  Container,
  HeaderCard,
  OrderCode,
  TimerBadge,
  BodyCard,
  InfoRow,
  CustomerName,
  TotalValue,
  TagsRow,
  Tag,
  Separator,
  FooterCard,
  ActionsRow,
  ActionBtn,
  NextStatusBtn,
  ExpandSection,
  ExpandLabel,
  ProductRow,
  ProductName,
  ProductQty,
  ProductPrice,
  ContactLine,
  StatusPaymentOnline,
  StatusLoyaltyBenefit,
} from './styles';
import { Modal, Button } from 'rsuite';
import api from '../../services/api';
import { formatCurrencyBRL } from '../../utils/utils';
import { CustomSelectPicker } from '../CustomSelectPicker';
import { useConfig } from '../../hooks/config';
import {
  FaTruckMoving,
  FaStore,
  FaUtensils,
  FaPrint,
  FaRegClock,
  FaChevronDown,
  FaChevronUp,
  FaArrowRight,
  FaAlignJustify,
  FaCheckCircle,
  FaCalendarAlt,
  FaGift,
} from 'react-icons/fa';
import { MdPayment } from 'react-icons/md';
import { Clock } from './Clock';
import { toastError, toastInfo } from '../../toasts';

const deliveryLabels = {
  EN: { label: 'Entrega', icon: FaTruckMoving },
  RE: { label: 'Retirada', icon: FaStore },
  LOCAL: { label: 'Consumir no Local', icon: FaUtensils },
  AG: { label: 'Agendamento', icon: FaCalendarAlt },
  AGRE: { label: 'Agendamento', icon: FaCalendarAlt },
};

const nextStatus = {
  PENDENTE: 'ANDAMENTO',
  ANDAMENTO: 'ENTREGA',
  ENTREGA: 'CONCLUIDO',
};

const nextStatusLabel = {
  PENDENTE: 'Aceitar',
  ANDAMENTO: 'Enviar',
  ENTREGA: 'Concluir',
};

function Card({ idPedido, data, onclick, type, atualizar, imprimir }) {
  const { config } = useConfig();
  const [expand, setExpand] = useState(true);
  const [entregadores, setEntregadores] = useState([]);
  const [entregadorId, setEntregadorId] = useState(null);
  const [dataPayload, setDataPayload] = useState({});
  const [open, setOpen] = useState(false);

  const pedidoProdutos = data?.detalhes ?? [];

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const dataEntregador = entregadores.map((item) => ({
    label: item.Nome,
    value: item.Id,
  }));

  const handleExpand = () => {
    setExpand((state) => !state);
  };

  const changeDeliveryType = async () => {
    try {
      const deliveryType = data.TipoEntrega === 'AG' ? 'EN' : 'RE';
      await api.post('pedido/alterarTipoEntrega', {
        TipoEntrega: deliveryType,
        Id: data.Id,
      });
      toastInfo('Pedido aceito!');
      atualizar();
    } catch (error) {
      console.log(error);
    }
  };

const mudaStatus = useCallback(async () => {
  if (!data) return;

  try {
    const isRetiradaAndamento =
      ['AGRE', 'RE', 'LOCAL'].includes(data.TipoEntrega) && data.Status === 'ANDAMENTO';
    const status = isRetiradaAndamento ? 'PRONTORETIRADA' : nextStatus[data.Status];

    if (status === 'ENTREGA' && data.TipoEntrega === 'EN') {
      setDataPayload({ Status: status, Id: idPedido });
      const res = await api.get(`Entregador/listar/${data.IdEmpresa}`);
      setEntregadores(res.data);
      toastInfo('Por favor, selecione o entregador.');
      handleOpen();
      return;
    }
    await api.post('pedido/alterar', { Status: status, Id: idPedido });
    toastInfo(`Pedido alterado para: ${status?.toLowerCase()}`);
    imprimir();
  } catch (error) {
    toastError('Não foi possível alterar o status do pedido.');
    console.error('Erro ao mudar status:', error.message);
  }
}, [data, idPedido, imprimir, handleOpen]);

  const handleNextStatus = () => {
    if (['AGRE', 'AG'].includes(data.TipoEntrega)) {
      changeDeliveryType();
    } else {
      mudaStatus();
    }
  };

  const changeWithDelivered = useCallback(
    async (e) => {
      if (!entregadorId) toastError('Selecione o entregador');
      try {
        await api.post('pedido/alterar', {
          ...dataPayload,
          IdEntregador: entregadorId,
        });
        toastInfo('Pedido alterado para: ' + dataPayload.Status.toLowerCase());
        handleClose(e);
        imprimir();
      } catch (error) {
        console.log(error.message);
      }
    },
    [entregadorId, dataPayload, imprimir],
  );

  const customerName = data?.Nome || data?.NomeSemCadastro?.split(' ')[0] || '';
  const phone = data?.CelularSemCadastro ?? data?.Telefone;
  const delivery = deliveryLabels[data?.TipoEntrega] || deliveryLabels.RE;
  const DeliveryIcon = delivery.icon;
  // Desconto já inclui o valor do benefício de fidelidade utilizado (exceto
  // frete grátis, que zera TaxaEntrega em vez de virar um desconto) — não
  // subtrair DescontoFidelidade de novo aqui, senão o benefício é descontado
  // duas vezes do total exibido.
  const totalCalc =
    +data.ValorTotal +
    +data.TaxaEntrega +
    (+data.TaxaAdministracao || 0) -
    (+data.Desconto || 0);
  const isPaidOnline = data?.AsaasPaymentId && data?.AsaasConfirmPayment;
  const usedLoyaltyBenefit = +data.DescontoFidelidade > 0;

  return (
    <>
      <Modal open={open} backdrop="static" size="xs" onClose={handleClose}>
        <Modal.Header>
          <Modal.Title>Selecione um entregador para continuar</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ display: 'flex', justifyContent: 'center' }}>
          <CustomSelectPicker
            data={dataEntregador}
            style={{ width: 350 }}
            defaultValue={entregadorId}
            onChange={setEntregadorId}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleClose} color="red" appearance="primary">
            Cancelar
          </Button>
          <Button onClick={changeWithDelivered} appearance="primary">
            Prosseguir
          </Button>
        </Modal.Footer>
      </Modal>

      <Container type={type}>
        {/* Header: Order code + Timer */}
        <HeaderCard>
          <OrderCode>#{data?.Codigo}</OrderCode>
          <TimerBadge>
            <FaRegClock size={11} />
            <Clock data={data} />
          </TimerBadge>
        </HeaderCard>

        {/* Body: Customer, Value, Tags */}
        <BodyCard>
          <InfoRow>
            <CustomerName>{customerName}</CustomerName>
            <TotalValue>{formatCurrencyBRL(totalCalc)}</TotalValue>
          </InfoRow>
          <TagsRow>
            <Tag highlight={data?.TipoEntrega === 'RE'}>
              <DeliveryIcon size={9} />
              {delivery.label}
            </Tag>
            <Tag>
              <MdPayment size={10} />
              {data?.FormaPagamento}
            </Tag>
            {isPaidOnline && (
              <StatusPaymentOnline>
                <FaCheckCircle size={8} />
                Pago Online
              </StatusPaymentOnline>
            )}
            {usedLoyaltyBenefit && (
              <StatusLoyaltyBenefit>
                <FaGift size={8} />
                Fidelidade
              </StatusLoyaltyBenefit>
            )}
            {+data.Troco > 0 && (
              <Tag>Troco: {formatCurrencyBRL(data.Troco)}</Tag>
            )}
          </TagsRow>
        </BodyCard>

        {/* Expand: Products, Contact, Payment details */}
        {expand && (
          <>
            <Separator />
            <ExpandSection>
              <ExpandLabel>Itens</ExpandLabel>
              {pedidoProdutos.map((produto, idx) => (
                <ProductRow key={idx}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ProductQty>{produto.Quantidade}x</ProductQty>
                    <ProductName>{produto.Descricao}</ProductName>
                  </div>
                  <ProductPrice>
                    {formatCurrencyBRL(produto.Valor)}
                  </ProductPrice>
                </ProductRow>
              ))}

              {phone && (
                <>
                  <ExpandLabel>Contato</ExpandLabel>
                  <ContactLine>
                    {customerName} - {phone}
                  </ContactLine>
                </>
              )}

              {+data.Desconto - (+data.DescontoFidelidade || 0) > 0 && (
                <>
                  <ExpandLabel>Desconto</ExpandLabel>
                  <ContactLine>
                    {formatCurrencyBRL(+data.Desconto - (+data.DescontoFidelidade || 0))}
                  </ContactLine>
                </>
              )}

              {usedLoyaltyBenefit && (
                <>
                  <ExpandLabel>Cashback resgatado</ExpandLabel>
                  <ContactLine>
                    {formatCurrencyBRL(data.DescontoFidelidade)}
                  </ContactLine>
                </>
              )}
            </ExpandSection>
          </>
        )}

        <Separator />

        <FooterCard>
          <ActionBtn
            onClick={handleExpand}
            title={expand ? 'Recolher' : 'Ver itens'}
          >
            {expand ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
          </ActionBtn>

          {!config?.DisableOrdersActions ? (
            <ActionsRow>
              <ActionBtn onClick={imprimir} title="Imprimir">
                <FaPrint size={12} />
              </ActionBtn>
              <ActionBtn onClick={onclick} title="Detalhes">
                <FaAlignJustify size={12} />
              </ActionBtn>
              <NextStatusBtn onClick={handleNextStatus}>
                {nextStatusLabel[data?.Status] || 'Avançar'}
                <FaArrowRight size={10} />
              </NextStatusBtn>
            </ActionsRow>
          ) : (
            <ActionsRow>
              <ActionBtn onClick={imprimir} title="Imprimir">
                <FaPrint size={12} />
              </ActionBtn>
              <ActionBtn
                onClick={handleExpand}
                style={{ visibility: 'hidden' }}
              />
            </ActionsRow>
          )}
        </FooterCard>
      </Container>
    </>
  );
}

export default Card;
